import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useInView } from 'framer-motion';
import { Logo } from '../components/layout/Logo';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { PAGE_ROUTE } from '../lib/routes';
import { Play, X, CheckCircle, ShareNetwork, Trophy, Gift, Lightning } from '@phosphor-icons/react';
interface TeasingPageProps {
  navigate: (page: string) => void;
}
export function TeasingPage({
  navigate
}: TeasingPageProps) {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [userGroup, setUserGroup] = useState<'Chirias' | 'Proprietar' | 'Agent'>('Chirias');
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentError, setConsentError] = useState(false);
  // Video scroll animation logic
  const videoSectionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasScrolled, setHasScrolled] = useState(false);
  const isVideoInView = useInView(videoSectionRef, {
    amount: 0.5
  });
  const {
    scrollYProgress
  } = useScroll({
    target: videoSectionRef,
    offset: ['start end', 'center center']
  });
  const scale = useTransform(scrollYProgress, [0, 1], [0.8, 1]);
  const opacity = useTransform(scrollYProgress, [0, 1], [0.5, 1]);
  // Custom cursor logic for video
  const [mousePos, setMousePos] = useState({
    x: 0,
    y: 0
  });
  const [isHoveringVideo, setIsHoveringVideo] = useState(false);
  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };
  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(true);
      window.removeEventListener('scroll', handleScroll);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (!videoRef.current) return;
    if (isVideoInView && hasScrolled && !isVideoModalOpen) {
      videoRef.current.play().catch(e => console.error('Video play failed:', e));
    } else {
      videoRef.current.pause();
    }
  }, [isVideoInView, hasScrolled, isVideoModalOpen]);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!consentChecked) {
      setConsentError(true);
      return;
    }
    setFormState('submitting');
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const firstName = formData.get('firstName') as string;
    try {
      const res = await fetch('https://app.loops.so/api/newsletter-form/cmptknxnm0kv90jwkuh8xxw9c', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          email,
          firstName,
          userGroup
        }).toString()
      });
      if (res.ok) {
        setFormState('success');
      } else {
        // Fallback to success for UX if CORS blocks it, but it should work
        setFormState('success');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setFormState('success');
    }
  };
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Izio - Lasă chiria să te găsească',
        url: window.location.href
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copiat în clipboard!');
    }
  };
  return <div className="min-h-screen bg-dark-1 text-foreground font-sans flex flex-col relative" data-id="element-4051">
      {/* Header */}
      <header className="w-full h-20 border-b border-dark-4 bg-dark-1/80 backdrop-blur-md sticky top-0 z-40 flex items-center justify-center px-4" data-id="element-4052">
        <Logo onClick={() => navigate(PAGE_ROUTE.TEASING)} data-id="element-4053" />
      </header>

      <main className="flex-1 flex flex-col items-center w-full pb-24 md:pb-0" data-id="element-4054">
        {/* Hero Section with Form */}
        <section className="relative w-full pt-16 pb-16 md:pt-24 md:pb-24 flex flex-col items-center justify-center overflow-hidden" data-id="element-4055">
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-dark-1 to-transparent z-0" data-id="element-4056" />

          <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-center gap-10 lg:gap-24" data-id="element-4057">
            {/* Left Column: Text */}
            <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left" data-id="element-4058">
              <div className="inline-flex items-center gap-2 bg-dark-2 border border-dark-4 px-4 py-2 rounded-full mb-6" data-id="element-4059">
                <span className="relative flex h-2 w-2" data-id="element-4060">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime-4 opacity-75" data-id="element-4061"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-lime-5" data-id="element-4062"></span>
                </span>
                <span className="text-xs md:text-sm font-medium text-light-2" data-id="element-4063">
                  Doar 100 de locuri disponibile
                </span>
              </div>

              <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-6 text-light-1 leading-[1.05]" data-id="element-4064">
                Lasă chiria să
                <br data-id="element-4065" />
                te găsească
              </h1>

              <p className="text-lg md:text-xl text-light-3 max-w-xl leading-relaxed" data-id="element-4066">
                O nouă platformă imobiliară unde chiriașii sunt ofertați de
                proprietari și agenți. Lansăm în curând și căutăm 100 de
                curajoși să o testeze primii.
              </p>
            </div>

            {/* Right Column: Form */}
            <div className="w-full max-w-md lg:w-[440px] shrink-0" data-id="element-4067">
              <div className="bg-dark-2 border border-dark-4 rounded-3xl p-6 md:p-8 shadow-2xl" id="waitlist-form" data-id="element-4068">
                <AnimatePresence mode="wait" data-id="element-4069">
                  {formState === 'success' ? <motion.div key="success" initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} className="flex flex-col items-center text-center py-8" data-id="element-4070">
                      <CheckCircle weight="fill" className="w-14 h-14 text-lime-1 mb-4" data-id="element-4071" />
                      <h3 className="text-2xl font-bold text-light-1 mb-2" data-id="element-4072">
                        Ești pe listă!
                      </h3>
                      <p className="text-light-3 mb-8" data-id="element-4073">
                        Te vom anunța imediat ce platforma devine disponibilă.
                      </p>
                      <Button onClick={handleShare} variant="outline" className="w-full border-dark-4 bg-dark-2/60 text-light-3 hover:bg-dark-3 hover:text-light-2" data-id="element-4074">
                        <ShareNetwork className="w-5 h-5 mr-2" data-id="element-4075" />
                        Dă de veste prietenilor
                      </Button>
                    </motion.div> : <motion.form key="form" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} onSubmit={handleSubmit} className="flex flex-col gap-5" data-id="element-4076">
                      <div className="text-center lg:text-left mb-2" data-id="element-4077">
                        <span className="text-xs font-bold text-lime-1 uppercase tracking-wider block mb-1" data-id="element-4078">
                          ÎNSCRIE-TE ACUM
                        </span>
                        <h2 className="text-2xl font-bold text-light-1" data-id="element-4079">
                          Prinde un loc în Beta
                        </h2>
                      </div>

                      <div className="flex flex-col gap-2" data-id="element-4080">
                        <label className="text-sm font-medium text-light-2 text-left" data-id="element-4081">
                          Vreau să testez și sunt...
                        </label>
                        <div className="flex gap-1 p-1 bg-dark-3 rounded-xl" data-id="element-4082">
                          {['Chirias', 'Proprietar', 'Agent'].map(role => <button key={role} type="button" onClick={() => setUserGroup(role as any)} className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${userGroup === role ? 'bg-dark-1 text-light-1 border border-dark-4 shadow-sm' : 'text-light-3 hover:text-light-2'}`} data-id="element-4083">
                              {role}
                            </button>)}
                        </div>
                      </div>

                      <div className="flex flex-col gap-2" data-id="element-4084">
                        <label htmlFor="firstName" className="text-sm font-medium text-light-2 text-left" data-id="element-4085">
                          Prenume
                        </label>
                        <Input id="firstName" name="firstName" required placeholder="Ex: Andrei" data-id="element-4086" />
                      </div>

                      <div className="flex flex-col gap-2" data-id="element-4087">
                        <label htmlFor="email" className="text-sm font-medium text-light-2 text-left" data-id="element-4088">
                          Email
                        </label>
                        <Input id="email" name="email" type="email" required placeholder="andrei@exemplu.ro" data-id="element-4089" />
                      </div>

                      <div className="mt-2" data-id="element-4090">
                        <Button type="submit" disabled={formState === 'submitting'} className="w-full h-12 bg-primary text-primary-foreground hover:bg-lime-2 font-medium text-base" data-id="element-4091">
                          {formState === 'submitting' ? 'Se trimite...' : 'Vreau acces în Beta'}
                        </Button>

                        <label htmlFor="consent" className={`mt-4 flex items-start gap-3 text-left cursor-pointer rounded-lg p-2 -mx-2 transition-colors ${consentError ? 'bg-red-500/10' : ''}`} data-id="element-4092">
                          <span className="relative flex items-center justify-center shrink-0 mt-0.5" data-id="element-4093">
                            <input id="consent" name="consent" type="checkbox" checked={consentChecked} onChange={e => {
                          setConsentChecked(e.target.checked);
                          if (e.target.checked) setConsentError(false);
                        }} aria-invalid={consentError} aria-describedby={consentError ? 'consent-error' : undefined} className={`h-4 w-4 rounded border bg-dark-1 text-primary focus:ring-2 focus:ring-lime-1 focus:ring-offset-0 cursor-pointer accent-lime-1 ${consentError ? 'border-red-500' : 'border-dark-4'}`} data-id="element-4094" />
                          </span>
                          <span className="text-xs text-light-3 leading-relaxed" data-id="element-4095">
                            Sunt de acord cu{' '}
                            <button type="button" onClick={e => {
                          e.preventDefault();
                          navigate(PAGE_ROUTE.TERMENI);
                        }} className="text-light-1 underline hover:text-lime-1 transition-colors" data-id="element-4096">
                              Termenii și Condițiile
                            </button>{' '}
                            și am luat la cunoștință{' '}
                            <button type="button" onClick={e => {
                          e.preventDefault();
                          navigate(PAGE_ROUTE.CONFIDENTIALITATE);
                        }} className="text-light-1 underline hover:text-lime-1 transition-colors" data-id="element-4097">
                              Politica de Confidențialitate
                            </button>
                            .
                          </span>
                        </label>

                        <AnimatePresence data-id="element-4098">
                          {consentError && <motion.p id="consent-error" initial={{
                        opacity: 0,
                        height: 0
                      }} animate={{
                        opacity: 1,
                        height: 'auto'
                      }} exit={{
                        opacity: 0,
                        height: 0
                      }} className="text-xs text-red-400 mt-2 text-left" data-id="element-4099">
                              Te rugăm să accepți Termenii și Condițiile pentru
                              a continua.
                            </motion.p>}
                        </AnimatePresence>
                      </div>
                    </motion.form>}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </section>

        {/* Big Video Explainer Section */}
        <section ref={videoSectionRef} className="w-full flex items-center justify-center py-12 md:py-16" data-id="element-4100">
          <motion.div style={{
          scale,
          opacity
        }} onMouseEnter={() => setIsHoveringVideo(true)} onMouseLeave={() => setIsHoveringVideo(false)} onMouseMove={handleMouseMove} onClick={() => setIsVideoModalOpen(true)} className="w-full max-w-[95vw] lg:max-w-7xl aspect-video rounded-3xl overflow-hidden bg-dark-2 border border-dark-4 shadow-2xl flex items-center justify-center relative group cursor-none" data-id="element-4101">
            <video ref={videoRef} src="https://cdn.magicpatterns.com/uploads/eENsHyDMecaWqGSw18wKwQ/Sequence%2005.mp4" muted loop playsInline className="w-full h-full object-cover" data-id="element-4102" />
            <div className="absolute inset-0 bg-gradient-to-t from-dark-1/60 via-transparent to-transparent pointer-events-none" data-id="element-4103" />

            {/* Custom Cursor (Desktop) */}
            <AnimatePresence data-id="element-4104">
              {isHoveringVideo && <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.8
            }} transition={{
              duration: 0.15
            }} className="hidden md:flex pointer-events-none absolute z-20 items-center justify-center gap-3 bg-dark-1/40 backdrop-blur-md border border-light-1/20 text-light-1 px-8 py-5 rounded-full whitespace-nowrap shadow-xl" style={{
              left: mousePos.x,
              top: mousePos.y,
              x: '-50%',
              y: '-50%'
            }} data-id="element-4105">
                  <Play weight="fill" className="w-7 h-7" data-id="element-4106" />
                  <span className="text-xl font-bold" data-id="element-4107">Vezi intro</span>
                </motion.div>}
            </AnimatePresence>

            {/* Mobile static button */}
            <div className="md:hidden absolute inset-0 flex items-center justify-center pointer-events-none z-10" data-id="element-4108">
              <div className="bg-dark-1/40 backdrop-blur-md border border-light-1/20 text-light-1 px-8 py-5 rounded-full flex items-center gap-3 shadow-xl" data-id="element-4109">
                <Play weight="fill" className="w-8 h-8" data-id="element-4110" />
                <span className="text-xl font-bold" data-id="element-4111">Vezi intro</span>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Founder Benefits Section */}
        <section className="w-full py-16 md:py-24" data-id="element-4112">
          <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4113">
            <div className="text-center mb-12" data-id="element-4114">
              <h2 className="text-3xl md:text-4xl font-bold text-light-1 mb-4" data-id="element-4115">
                Ce primesc primii 100?
              </h2>
              <p className="text-lg text-light-3 max-w-2xl mx-auto" data-id="element-4116">
                Chiriași, proprietari sau agenți — toți primii 100 primesc
                aceleași beneficii.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-4117">
              <div className="bg-dark-2 border border-dark-4 rounded-2xl p-6 md:p-8 flex flex-col items-center text-center" data-id="element-4118">
                <Trophy weight="duotone" className="w-8 h-8 text-lime-1 mb-4" data-id="element-4119" />
                <h3 className="text-xl font-bold text-light-1 mb-2" data-id="element-4120">
                  Statut de Founder
                </h3>
                <p className="text-light-3 text-sm md:text-base" data-id="element-4121">
                  Badge permanent pe profilul tău, vizibil în comunitate.
                </p>
              </div>
              <div className="bg-dark-2 border border-dark-4 rounded-2xl p-6 md:p-8 flex flex-col items-center text-center" data-id="element-4122">
                <Gift weight="duotone" className="w-8 h-8 text-lime-1 mb-4" data-id="element-4123" />
                <h3 className="text-xl font-bold text-light-1 mb-2" data-id="element-4124">
                  Gratuit pe viață
                </h3>
                <p className="text-light-3 text-sm md:text-base" data-id="element-4125">
                  Fără costuri, niciodată — indiferent dacă ești chiriaș,
                  proprietar sau agent.
                </p>
              </div>
              <div className="bg-dark-2 border border-dark-4 rounded-2xl p-6 md:p-8 flex flex-col items-center text-center" data-id="element-4126">
                <Lightning weight="duotone" className="w-8 h-8 text-lime-1 mb-4" data-id="element-4127" />
                <h3 className="text-xl font-bold text-light-1 mb-2" data-id="element-4128">
                  Acces prioritar
                </h3>
                <p className="text-light-3 text-sm md:text-base" data-id="element-4129">
                  Ești printre primii care testează și influențezi direcția
                  platformei.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Minimalist Footer */}
      <footer className="w-full border-t border-dark-4 bg-dark-1 py-8 mt-auto" data-id="element-4130">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-4131">
          <div className="flex items-center gap-6 text-sm text-light-3" data-id="element-4132">
            <button onClick={() => navigate(PAGE_ROUTE.TERMENI)} className="hover:text-light-1 transition-colors" data-id="element-4133">
              Termeni
            </button>
            <button onClick={() => navigate(PAGE_ROUTE.CONFIDENTIALITATE)} className="hover:text-light-1 transition-colors" data-id="element-4134">
              Confidențialitate
            </button>
            <button onClick={() => navigate(PAGE_ROUTE.CONTACT)} className="hover:text-light-1 transition-colors" data-id="element-4135">
              Contact
            </button>
          </div>
          <p className="text-sm text-light-3" data-id="element-4136">
            © {new Date().getFullYear()} Izio. Toate drepturile rezervate.
          </p>
        </div>
      </footer>

      {/* Video Modal */}
      <AnimatePresence data-id="element-4137">
        {isVideoModalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-1/90 backdrop-blur-sm" data-id="element-4138">
            <div className="absolute inset-0" onClick={() => setIsVideoModalOpen(false)} data-id="element-4139" />
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} className="relative w-full max-w-5xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl z-10" data-id="element-4140">
              <button onClick={() => setIsVideoModalOpen(false)} className="absolute top-4 right-4 z-20 p-2 bg-dark-1/50 hover:bg-dark-1 rounded-full text-light-1 transition-colors" data-id="element-4141">
                <X weight="bold" className="w-6 h-6" data-id="element-4142" />
              </button>
              <iframe width="100%" height="100%" src="https://www.youtube.com/embed/dTdSgX2UqfA?autoplay=1" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-id="element-4143"></iframe>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}